import { useTranslation } from "react-i18next";
import { useCountry } from "@/lib/CountryContext";
import { currencySymbol } from "@/lib/currency";

/**
 * Localised hero copy for the current country.
 *
 * Lookup cascade (per key):
 *   1. `hero_variants.<COUNTRY>.<key>` — bespoke per-market copy
 *   2. `hero.<key>` — generic fallback (English defaults for markets
 *      that don't have a bespoke variant yet)
 *
 * Popular categories: array of stringified category names, or empty
 * array if the market hasn't defined any yet. The Landing hero renders
 * them as a chip row below the CTAs.
 *
 * `currencyNote`: injected symbol via `currencySymbol()` so we don't
 * have to hand-write "All prices in HK$" 12 times per language.
 */
export default function useLocalizedHero() {
    const { t, i18n } = useTranslation();
    const {
        country,
        countryInfo,
        heroVideoUrl,
        heroPosterUrl,
        heroCopyByCode,
    } = useCountry();

    // Admin-authored DB overrides for THIS country + current UI language.
    // Any non-empty field wins over both `hero_variants` and `hero.*`.
    const currentLang = i18n.resolvedLanguage || i18n.language || "en";
    const dbOverride = heroCopyByCode?.[country]?.[currentLang] || {};

    const variantExists = i18n.exists(`hero_variants.${country}.title_1`);

    const key = (name) =>
        variantExists ? `hero_variants.${country}.${name}` : `hero.${name}`;

    // Cascade: DB override → i18n variant → generic. Empty strings in
    // the override are treated as "not set" so admins can blank a
    // single field to fall back to the default without wiping the row.
    const resolved = (name) => {
        const v = dbOverride[name];
        return v && String(v).trim() ? v : t(key(name));
    };

    const categories = t(`hero_variants.${country}.popular_categories`, {
        returnObjects: true,
        defaultValue: [],
    });

    // If popular_categories missing OR the returned value isn't an
    // array (i18next sometimes returns the key itself as a string when
    // the resource is missing), coerce to [].
    const popularCategories = Array.isArray(categories) ? categories : [];

    return {
        country,
        countryName: countryInfo.name,
        countryFlag: countryInfo.flag,
        eyebrow: resolved("eyebrow"),
        title1: resolved("title_1"),
        titleEm: resolved("title_em"),
        title2: resolved("title_2"),
        subtitle: resolved("subtitle"),
        ctaSignup: t("hero.cta_signup"),
        ctaBrowse: t("hero.cta_browse"),
        ratingText: t("hero.rating_text"),
        currencyNote: t("hero.currency_note", {
            defaultValue: "All prices shown in {{symbol}}",
            symbol: currencySymbol(country),
        }),
        popularCategories,
        hasBespokeVariant: variantExists,
        // Per-country hero video/poster from admin panel. Both fall back
        // to the default HK-branded assets shipped in /public/panda/.
        videoUrl: heroVideoUrl || "/panda/hero-animation.mp4",
        posterUrl: heroPosterUrl || "/panda/hero-animation.gif",
        hasBespokeVideo: !!heroVideoUrl,
    };
}
